import { STORAGE_KEYS } from '@/constants/CheckoutConstants.js'
import { useCustomerStore } from '@/stores/customer'
import {
  StorageSerializers,
  useLocalStorage,
  useSessionStorage
} from '@vueuse/core'
import { defineStore } from 'pinia'

const VIC_VENDOR_ID = 452 // Vicampo vendor ID
const SESSION_STORAGE_KEYS = {
  LAST_CART_UPDATE: 'last_cart_update',
  COUPON: 'coupon',
  USE_CREDITS: 'use_credits'
}
const CART_SESSION_VALIDITY = 1000 * 60 * 60 * 3 // 3h

const _getDefaultState = () => ({
  instance: {
    id: null,
    canCheckout: false,
    undiscountedGrandTotal: 0,
    grandTotal: 0,
    shippingTotal: null,
    shippingDestinationFull: '',
    taxTotal: 0,
    milesAndMoreData: null,
    products: [],
    vendors: [],
    discounts: []
  },

  calculated: {
    itemCount: 0,
    productsByVendor: {},
    indexesByVendor: {},
    grandTotalFormatted: null,
    shippingTotalFormatted: '',
    valueOfGoods: 0,
    valueOfGoodsFormatted: '',
    taxTotalFormatted: '',
    discount: 0,
    formattedDiscount: ''
  },
  updated_at: null,
  updating: true,
  storedLastUpdate: useLocalStorage(
    SESSION_STORAGE_KEYS.LAST_CART_UPDATE,
    null,
    {
      serializer: {
        read: (value) => (value ? new Date(value) : null),
        write: (value) => (value ? value.toISOString() : null)
      }
    }
  ),
  storedCouponCode: useLocalStorage(SESSION_STORAGE_KEYS.COUPON, null),
  couponCodeError: null,
  storedUseCredits: useLocalStorage(SESSION_STORAGE_KEYS.USE_CREDITS, true, {
    serializer: StorageSerializers.boolean
  }),
  storedMiles: localStorage.getItem('miles'),
  selectedDeliveryOptionsMap: useSessionStorage(
    STORAGE_KEYS.SELECTED_DELIVERY_OPTIONS_MAP,
    null,
    { serializer: StorageSerializers.object }
  ),
  deliveryOptions: []
})

export const useCartStore = defineStore('cart', {
  state: () => _getDefaultState(),
  getters: {
    cartId: ({ instance: { id } }) => id,
    lastUpdate: ({ updated_at }) => updated_at,
    cartInstance: ({ instance, calculated }) => ({
      ...instance,
      ...calculated
    }),
    isUpdating: ({ updating }) => updating,
    vendors: ({ instance: { vendors } }) => vendors,
    vendorItems:
      ({ calculated: { productsByVendor } }) =>
      (id) =>
        productsByVendor[id],
    vendorIndex:
      ({ calculated: { indexesByVendor } }) =>
      (id) =>
        indexesByVendor[id],
    productInCart:
      ({ instance: { products } }) =>
      (productId) =>
        products.some((id) => Number(id) === Number(productId)),
    firstVendorWithMessages: ({ instance: { vendors } }) =>
      vendors.filter(({ messages }) => messages.length)[0],
    hasOtherVendorsThan:
      ({ instance: { vendors } }) =>
      (vendorName) =>
        vendors.some(({ name }) => name !== vendorName),
    itemCount: ({ calculated: { itemCount } }) => itemCount,
    campaignItemCounts: ({ calculated: { campaignItemCounts } }) =>
      campaignItemCounts,
    valueOfGoods: ({ calculated: { valueOfGoods } }) => valueOfGoods,
    discount: ({ calculated: { discount } }) => discount,
    formattedDiscount: ({ calculated: { formattedDiscount } }) =>
      formattedDiscount,
    hasMessages: ({ firstVendorWithMessages }) => !!firstVendorWithMessages,
    taxTotalFormatted: ({ calculated: { taxTotalFormatted } }) =>
      taxTotalFormatted,
    couponDiscount: ({ instance: { discounts } }) => {
      return discounts.find((discount) => discount.type === 'coupon') || null
    },
    creditDiscount: ({ instance: { discounts } }) => {
      return discounts.find((discount) => discount.type === 'credits') || null
    },
    miles: ({ storedMiles }) => storedMiles,
    discounts: ({ instance: { discounts } }) => discounts,
    creditsAreUsed: ({ storedUseCredits }) => storedUseCredits,
    hasProductsInCart: ({ instance: { products } }) =>
      products && products.length > 0,
    vicampoDeliveryOptionId: ({ selectedDeliveryOptionsMap }) => {
      return selectedDeliveryOptionsMap?.[VIC_VENDOR_ID] || null
    },
    canCheckout: ({ instance: { canCheckout } }) => canCheckout
  },
  actions: {
    async reset() {
      if (this.router.currentRoute.value.name !== 'cart') return

      const { instance, calculated } = _getDefaultState()

      this.instance = instance
      this.calculated = calculated
      this.storedMiles = null
      this.storedLastUpdate = null
      await this.refreshCart()
    },
    async setupState(data) {
      const { instance, calculated } = await _getMutationObject(data)
      this.instance = instance
      this.calculated = calculated
      this.storedLastUpdate = new Date()
    },
    async refreshCart(retryCount = 0) {
      this.updating = true

      this.validateSessionData()

      const cartId = await _createOrGetCartInstance()
      const {
        ApiController,
        TrackingHelper,
        MessageHelper,
        TranslationsHelper
      } = await _getModules()
      try {
        const customerStore = useCustomerStore()
        await customerStore.waitUntilPending()

        void ApiController.sendCouponCode(this.storedCouponCode)

        const data = await ApiController.refreshCart(
          cartId,
          this.storedCouponCode,
          this.storedUseCredits,
          this.vicampoDeliveryOptionId,
          this.storedMiles
        )
        await this.setupState(data, cartId)

        TrackingHelper.sendUtmParams()
      } catch (e) {
        // Handle cart not belonging to user
        if (e.cause === 1050) {
          if (retryCount === 0) {
            await this._handleCartOwnershipError()
            this.updating = false
            return this.refreshCart(1)
          }
        }

        switch (e.cause) {
          case 1030:
            this.couponCodeError = 'coupon_not_found'
            MessageHelper.addMessage(
              TranslationsHelper.translate('coupon_not_found'),
              'icon_svg_alert'
            )
            this.removeCouponCode()
            break
          case 1031:
            this.couponCodeError = 'coupon_not_applicable'
            MessageHelper.addMessage(
              TranslationsHelper.translate('coupon_not_applicable'),
              'icon_svg_alert'
            )
            this.removeCouponCode()
            break
          default:
            console.error(e, 'Error at cart [refreshCart]')
        }
      }

      this.updating = false
    },
    async addItem(productData, showMessage = true) {
      this.updating = true
      const { productId, campaignId, qty, upsellId } = productData

      const { UtilityHelper, ApiController, PAGE_URL } = await _getModules()
      try {
        const data = await ApiController.addItemToCart(
          await _createOrGetCartInstance(),
          {
            product_id: productId,
            campaign_id: campaignId,
            qty: qty,
            upsell_id: upsellId
          },
          this.storedCouponCode,
          this.storedUseCredits
        )
        await this.setupState(data)

        if (showMessage) {
          _addMessage(
            'header_cart_add_to_cart_success_message',
            'icon_svg_cart',
            PAGE_URL.CART
          )
        }

        void _trackAddItem(productData)
      } catch (e) {
        _addMessage('header_cart_add_to_cart_failure_message', 'icon_svg_info')
        UtilityHelper.noticeNRError(e, 'Error at cart [addItemToCart] ')
      }

      this.updating = false
    },
    async updateItem({ item, qty, itemListId, index }) {
      this.updating = true

      const { ApiController, UtilityHelper } = await _getModules()

      try {
        const data = await ApiController.updateCartItem(
          await _createOrGetCartInstance(),
          {
            cartItemId: item.cart_item_id,
            qty
          },
          this.storedCouponCode,
          this.storedUseCredits
        )
        await this.setupState(data)

        _addMessage('new_cart_page_update_success', 'icon_svg_checkmark')
        void _trackUpdateItem(item, qty, item.qty, itemListId, index)
      } catch (e) {
        _addMessage('new_cart_page_update_fail', 'icon_svg_alert')
        UtilityHelper.noticeNRError(e, 'Error at cart [updateItem]')
      }
      this.updating = false
    },
    async removeItem({ item, itemListId, index }) {
      this.updating = true

      const { ApiController, UtilityHelper } = await _getModules()

      try {
        const data = await ApiController.removeCartItem(
          await _createOrGetCartInstance(),
          item.cart_item_id,
          this.storedCouponCode,
          this.storedUseCredits
        )
        await this.setupState(data)

        _addMessage('new_cart_page_remove_success', 'icon_svg_checkmark')
        void _trackRemoveItem(item, itemListId, index)
      } catch (e) {
        UtilityHelper.noticeNRError(e, 'Error at cart [removeItem]')
        _addMessage('new_cart_page_remove_fail', 'icon_svg_alert')
      }

      this.updating = false
    },
    async fetchDeliveryOptions() {
      const { ApiController } = await _getModules()
      this.deliveryOptions = await ApiController.fetchDeliveryOptions(
        await _createOrGetCartInstance()
      )
    },
    applyCouponCode(code) {
      if (!code) return
      this.storedUseCredits = false
      this.storedCouponCode = code
      this.refreshCart()
    },
    removeCouponCode() {
      this.storedCouponCode = null
      this.refreshCart()
    },
    applyCreditsToCart() {
      this.storedUseCredits = true
      this.storedCouponCode = null
      this.refreshCart()
    },
    removeCreditsFromCart() {
      this.storedUseCredits = false
      this.refreshCart()
    },
    applyMilesToCart(miles) {
      localStorage.setItem('miles', miles)
      this.storedMiles = miles
      this.refreshCart()
    },
    removeMilesFromCart() {
      localStorage.removeItem('miles')
      this.storedMiles = null
      this.refreshCart()
    },
    async waitForUpdateFinished() {
      while (this.updating) await _wait()
      return Promise.resolve()
    },
    async _handleCartOwnershipError() {
      this.instance.id = null

      const { CookieHelper } = await _getModules()
      // Invalidate cart cookie and create new cart for both soft login and regular users
      CookieHelper.invalidate('cartUuid')
      console.warn('Cart ownership conflict detected, creating new cart')
    },
    validateSessionData() {
      if (this.storedLastUpdate) {
        const now = new Date()
        const diff = now - this.storedLastUpdate
        if (diff > CART_SESSION_VALIDITY) {
          this.storedCouponCode = null
          this.storedUseCredits = true
        }
      }
    }
  }
})

const _addMessage = (messageKey, icon, url = null) => {
  setTimeout(async () => {
    const { MessageHelper } = await _getModules()
    MessageHelper.addMessage(messageKey, icon, url)
  }, 0)
}

const _getMutationObject = async (data) => {
  const {
    uuid,
    vendors,
    products,
    shipping_total: shippingTotal,
    discount_value: discount,
    value_of_goods: valueOfGoods,
    item_count: itemCount,
    can_checkout: canCheckout,
    undiscounted_grand_total: undiscountedGrandTotal,
    grand_total: grandTotal,
    miles_and_more: milesAndMoreData,
    tax_total: taxTotal,
    shipping_destination_full: shippingDestinationFull,
    discounts
  } = data

  const { TranslationsHelper, fetchProductWithIndex, CookieHelper, domain } =
    await _getModules()

  const customerStore = useCustomerStore()
  const grandTotalFormatted = TranslationsHelper.formatCurrency(grandTotal)
  const shippingTotalFormatted =
    TranslationsHelper.formatCurrency(shippingTotal)
  const valueOfGoodsFormatted = TranslationsHelper.formatCurrency(valueOfGoods)
  const taxTotalFormatted = TranslationsHelper.formatCurrency(taxTotal)
  const formattedDiscount = TranslationsHelper.formatCurrency(discount * -1)

  const indexedProducts = await fetchProductWithIndex(
    vendors.reduce((items, vendor) => [...items, ...vendor.items], []),
    customerStore.customerIsAdmin
  )
  const productsByVendor = await _getProductsByVendor(vendors, indexedProducts)
  const productsByVendorWithUpsell = await _getProductsByVendor(
    vendors,
    indexedProducts,
    true
  )

  const indexesByVendor = {}
  let start = 0
  for (const [id, items] of Object.entries(productsByVendor)) {
    indexesByVendor[id] = start
    start += items.length
  }

  if (uuid !== 'null') {
    CookieHelper.setCookie('cartUuid', uuid, 365, domain)
  }

  return {
    instance: {
      id: uuid,
      canCheckout,
      undiscountedGrandTotal,
      grandTotal,
      shippingTotal,
      shippingDestinationFull,
      taxTotal,
      milesAndMoreData,
      products,
      vendors,
      discounts
    },
    calculated: {
      productsByVendor,
      productsByVendorWithUpsell,
      indexesByVendor,
      itemCount,
      grandTotalFormatted,
      shippingTotalFormatted,
      valueOfGoods,
      valueOfGoodsFormatted,
      taxTotalFormatted,
      discount,
      formattedDiscount
    }
  }
}

const _getProductsByVendor = async (
  vendors,
  indexedProducts,
  withUpsell = false
) => {
  const { matchItemWithProduct } = await _getModules()
  return vendors.reduce(
    (c, { vendor_id: id, items }) => ({
      ...c,
      [id]: matchItemWithProduct(
        (items ?? []).reduce(
          (c, item) => (item.is_upsell && !withUpsell ? c : [...c, item]),
          []
        ),
        indexedProducts,
        { endpoint: 'cart' }
      )
    }),
    {}
  )
}

const _createOrGetCartInstance = async () => {
  const { CookieHelper, UtilityHelper, domain } = await _getModules()
  let id = CookieHelper.getCookie('cartUuid')
  if (!id || id === 'null') {
    id = UtilityHelper.uuid()
    CookieHelper.setCookie('cartUuid', id, 365, domain)
  }
  return id
}

const _trackAddItem = async (data) => {
  const trackingData = await _getTrackingData(data)
  const { default: GA4Controller } = await import(
    '@/services/analytics/GA4Controller'
  )
  await GA4Controller.pushAddToCartEvent(trackingData)
}

const _trackUpdateItem = async (item, newQty, oldQty, itemListId, index) => {
  const { GtmCartPage } = await _getModules()
  GtmCartPage.changeQty(item, { newQty, oldQty, itemListId, index })
}

const _trackRemoveItem = async (item, itemListId, index) => {
  if (!itemListId) return
  const { GtmCartPage, AnalyticsItemFactory } = await _getModules()
  const analyticsItem = AnalyticsItemFactory.getAnalyticsItem({
    item,
    itemListId,
    quantity: item.qty,
    index
  })
  GtmCartPage.deleteCartItem({ analyticsItem, itemListId })
}

const _getTrackingData = async ({
  productId,
  name,
  campaignId,
  manufacturer,
  variant,
  price,
  originalPrice,
  qty,
  itemListId,
  index
}) => {
  const { EcProductFactory, AnalyticsItemFactory } = await _getModules()

  const ecProduct = EcProductFactory.getEcProduct(
    productId,
    name,
    campaignId,
    manufacturer,
    variant,
    price,
    originalPrice,
    qty
  )
  const analyticsItem = AnalyticsItemFactory.getAnalyticsItem({
    item: ecProduct,
    itemListId,
    index
  })

  return { ecProduct, analyticsItem, itemListId }
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { fetchProductWithIndex, matchItemWithProduct },
      { PAGE_URL, PRODUCT_TYPES },
      { default: TranslationsHelper },
      { default: UtilityHelper },
      { default: ApiController },
      { default: TrackingHelper },
      { default: EcProductFactory },
      { default: AnalyticsItemFactory },
      { default: MessageHelper },
      { default: CookieHelper },
      {
        default: { domain }
      },
      { default: GtmCartPage }
    ] = await Promise.all([
      await import('@/services/helpers/BulkProductMatcher'),
      await import('@/constants/GlobalConstants'),
      await import('@/services/helpers/TranslationsHelper'),
      await import('@/services/helpers/UtilityHelper'),
      await import('@/services/ApiController'),
      await import('@/services/helpers/TrackingHelper'),
      await import('@/modules/tag_manager/EcProductFactory'),
      await import('@/modules/tag_manager/AnalyticsItemFactory'),
      await import('@/services/helpers/MessageHelper'),
      await import('@/services/helpers/CookieHelper'),
      await import('@/services/SiteConfig'),
      await import('@/services/analytics/handlers/GtmCartPage')
    ])
    cache = {
      fetchProductWithIndex,
      matchItemWithProduct,
      PAGE_URL,
      PRODUCT_TYPES,
      TranslationsHelper,
      UtilityHelper,
      ApiController,
      TrackingHelper,
      EcProductFactory,
      AnalyticsItemFactory,
      MessageHelper,
      CookieHelper,
      domain,
      GtmCartPage
    }
    return cache
  }
})()

const _wait = async () =>
  new Promise((resolve) => {
    setTimeout(resolve, 100)
  })
